import AppRouter from 'common/AppRouter';
import Home from 'views/root/views/home';
import Onboarding from 'views/root/views/onboarding';
import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { getUserTrackingId } from '@linearb/linjs-common';
import * as TrackingService from 'common/services/tracking.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { CenterLoader } from 'connected-components/Loader/Loader';
import { handleActiveTeam, initPusher } from './root.service';
import { LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import {
  getToken,
  refreshAccessTokenOnExpiration
} from 'common/services/token.service';
import { JOB_NAMES } from 'common/services/jobs-suites.service';
import { useHistory } from 'react-router';
import { setRumUser } from 'common/services/rum.service';

const goToSavedPathIfNeeded = (history, organizationId) => {
  if (!history.location.pathname || history.location.pathname === '/') {
    const savedPath = BrowserStorageAccessor(organizationId).local.get(
      LOCAL_STORAGE_KEYS.LAST_VIEW_MAIN_PAGE
    );
    if (savedPath) {
      history.replace(savedPath);
    }
  }
};

export const Root = ({
  getConfiguration,
  getAccountByToken,
  getRepositories,
  getSensorAuthorizations,
  getOrganization,
  selectTeamId,
  getJobDetails
}) => {
  const history = useHistory();
  const [loadedData, setLoadedData] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        refreshAccessTokenOnExpiration(getToken());
        const configuration = await getConfiguration();
        const account = await getAccountByToken();

        if (!account) {
          // Root.js should not do anything in case account is undefined
          // The state will be handled by TokenHandlerHOC or /overdue mode in case of expired trial
          return;
        }
        const {
          organization_id: orgId,
          name = '',
          type_id,
          created_at,
          last_logged_in_at
        } = account;
        const organization = orgId ? await getOrganization() : null;
        const isSettingsPage = history.location.pathname.includes('/settings');
        const sensorAuthorizations = organization
          ? await getSensorAuthorizations({
              withGitStreamEnabled: isSettingsPage
            })
          : [];
        const repositories = sensorAuthorizations.length
          ? await getRepositories({ limit: 10 })
          : [];

        if (sensorAuthorizations.length) {
          // wait for getJobDetails call only if the organization is not finished onboarding, otherwise do it in the background
          if (!organization.finish_on_boarded_at) {
            await getJobDetails(JOB_NAMES.REPOSITORIES_COLLECTOR);
          } else {
            getJobDetails(JOB_NAMES.REPOSITORIES_COLLECTOR);
          }

          // Silently fetch all repositories without freeze the ui
          getRepositories();
        }

        if (organization) {
          handleActiveTeam(organization, selectTeamId);
        }

        setLoadedData(true);

        // Identify user to trackers
        const { userId, email } = getUserTrackingId(account);
        const trackingProperties = {
          user: { userId, email },
          attrs: {
            user_type: type_id,
            name,
            created: created_at,
            last_login: last_logged_in_at
          }
        };

        TrackingService.setUserId({ userId, email });
        TrackingService.setUserProperties(trackingProperties);

        setRumUser(userId, email, trackingProperties.attrs);

        if (!orgId || !sensorAuthorizations.length || !repositories.length) {
          history.replace(AppRouter.ONBOARDING.path);
          return;
        }

        // init Pusher
        // When onBoarding we don't reach this code because we don't have repositories yet.
        // We will return in the if above when we check !repositories.length
        if (orgId && configuration) {
          initPusher(account, configuration);
        }

        goToSavedPathIfNeeded(history, orgId);
      } catch (e) {
        // If from some reason the promise of org/account/sensor auth/repos get rejected -> redirect to onboarding flow
        history.replace(AppRouter.ONBOARDING.path);
      }
    })();
  }, []);

  return loadedData ? (
    <Switch>
      <Route path={AppRouter.ONBOARDING.path} component={Onboarding} />
      <Route path={AppRouter.ROOT.path} component={Home} />
    </Switch>
  ) : (
    <CenterLoader />
  );
};

export default Root;
